import { metaLazy } from "@/lib/metaLazy";
import { compressImageBeforeUpload } from "@/lib/compress-image";
import { buildStoragePath } from "@/lib/upload-guard";
import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "@/lib/toast";
import { SiteShell } from "@/components/SiteShell";
import { PageHero } from "@/components/PageHero";
import { LinkedTerms } from "@/components/LinkedTerms";
import { SHOW_UPPROMOTE_PUBLIC } from "@/lib/linked-terms";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLocale } from "@/contexts/LocaleContext";
import { DICTS } from "@/lib/i18n";
import { submitAmbassadorApplication } from "@/lib/ambassador-applications.functions";
import { smartBackOnClick } from "@/lib/smartBack";
import { parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js";
import { LECMO_WA_NUMBER } from "@/lib/agentConfig";
import {
  MessageCircle,
  Plus,
  X,
  Instagram,
  Youtube,
  Twitter,
  Music2,
  Ghost,
  Star,
  ExternalLink,
  ArrowUp,
  Globe,
  Upload,
  CheckCircle2,
  Loader2,
  Sparkles,
  TrendingUp,
  Crown,
  ChevronDown,
} from "lucide-react";
import { useExclusiveDetails } from "@/lib/useExclusiveDetails";
import { trackWhatsAppClick } from "@/lib/ga4";
import { buildProfileUrl, normalizeProfileUrl } from "@/lib/socialPlatforms";
import { COUNTRIES, DIAL_CODES, getDialCode } from "@/lib/countries";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import loverSocialImage from "@/assets/ambassador-tier-lover.png.asset.json";
import creatorImage from "@/assets/ambassador-tier-creator.png.asset.json";
import superstarImage from "@/assets/ambassador-superstar.jpg";
import { Link } from "@tanstack/react-router";
import { useAuth } from "@/contexts/AuthContext";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
import { AmbassadorLifeCircle } from "@/components/ambassador/AmbassadorLifeCircle";
import { AmbassadorStagesBenefits } from "@/components/ambassador/AmbassadorStagesBenefits";




export const Route = createFileRoute("/ambassadors/")({
  head: () => ({
    meta: [
      { title: DICTS.en["ambassadors.meta.title"] },
      { name: "description", content: DICTS.en["ambassadors.meta.description"] },
      { property: "og:title", content: DICTS.en["ambassadors.meta.title"] },
      { property: "og:description", content: DICTS.en["ambassadors.meta.description"] },
      { property: "og:url", content: `${PUBLIC_SITE_ORIGIN}/ambassadors` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/ambassadors` }],
  }),
  component: AmbassadorsPage,
});

const PLATFORMS = ["instagram", "tiktok", "youtube", "snapchat", "twitter", "other"] as const;
type Platform = (typeof PLATFORMS)[number];
type UploadState = "idle" | "uploading" | "done" | "error";
type Account = {
  platform: Platform;
  handle: string;
  profile_url: string;
  insights_url: string;
  /** Local blob preview (bucket is private, so no public URL exists). */
  insights_preview?: string;
  insights_state: UploadState;
  followers: string;
};

const CONTENT_CATEGORIES = [
  "fashion_beauty",
  "food_cuisine",
  "travel_tourism",
  "technology",
  "sports_fitness",
  "lifestyle",
  "education",
  "games",
  "music_art",
  "business_finance",
  "other",
] as const;
type ContentCategory = (typeof CONTENT_CATEGORIES)[number];

/**
 * أيقونات المنصّات بألوان الهوية الرسميّة لكلّ منصّة.
 * هذه استثناء مقصود من قاعدة tokens لأنّها ألوان علامات تجاريّة خارجيّة ثابتة.
 */
function PlatformIcon({
  platform,
  className,
  colored = true,
}: {
  platform: Platform;
  className?: string;
  colored?: boolean;
}) {
  const cls = className ?? "h-4 w-4";
  if (!colored) {
    const Icon =
      platform === "instagram"
        ? Instagram
        : platform === "tiktok"
        ? Music2
        : platform === "youtube"
        ? Youtube
        : platform === "snapchat"
        ? Ghost
        : platform === "twitter"
        ? Twitter
        : Globe;
    return <Icon className={cls} />;
  }
  switch (platform) {
    case "instagram":
      return (
        <span
          className={cn(
            "inline-flex items-center justify-center rounded-none text-white",
            cls,
          )}
          style={{
            background:
              "linear-gradient(45deg,#f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)",
          }}
        >
          <Instagram className="h-[70%] w-[70%]" />
        </span>
      );
    case "tiktok":
      return (
        <span className={cn("inline-flex items-center justify-center rounded-none bg-black text-white", cls)}>
          <Music2 className="h-[70%] w-[70%]" />
        </span>
      );
    case "youtube":
      return (
        <span className={cn("inline-flex items-center justify-center rounded-none text-white", cls)} style={{ background: "#FF0000" }}>
          <Youtube className="h-[70%] w-[70%]" />
        </span>
      );
    case "snapchat":
      return (
        <span className={cn("inline-flex items-center justify-center rounded-none text-black", cls)} style={{ background: "#FFFC00" }}>
          <Ghost className="h-[70%] w-[70%]" />
        </span>
      );
    case "twitter":
      return (
        <span className={cn("inline-flex items-center justify-center rounded-none text-white", cls)} style={{ background: "#000000" }}>
          <Twitter className="h-[70%] w-[70%]" />
        </span>
      );
    default:
      return (
        <span className={cn("inline-flex items-center justify-center rounded-none bg-muted text-muted-foreground", cls)}>
          <Globe className="h-[70%] w-[70%]" />
        </span>
      );
  }
}

function AmbassadorsPage() {
  const { t, locale } = useLocale();
  const { user } = useAuth();
  const submit = useServerFn(submitAmbassadorApplication);
  const tiersContainerRef = useExclusiveDetails<HTMLDivElement>();

  const [form, setForm] = React.useState({
    email: "",
    display_name: "",
    audience_country: "AE",
    phone_dial_country: "AE",
    phone_local: "",
    content_category: "" as ContentCategory | "",
    pitch: "",
  });
  const [accounts, setAccounts] = React.useState<Account[]>([
    {
      platform: "instagram",
      handle: "",
      profile_url: "",
      insights_url: "",
      insights_state: "idle",
      followers: "",
    },
  ]);
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState<"new" | "existing" | null>(null);
  const [referenceCode, setReferenceCode] = React.useState<string | null>(null);
  const [showForm, setShowForm] = React.useState(false);
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const formRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (user?.email) setForm((f) => (f.email ? f : { ...f, email: user.email as string }));
  }, [user?.email]);

  const openSuperStarForm = () => {
    setShowForm(true);
    // عطِ React وقتاً ليُركّب القسم قبل التمرير
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const onChange = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const updateAccount = (i: number, patch: Partial<Account>) =>
    setAccounts((arr) => arr.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));

  const addAccount = () => {
    if (accounts.length >= 8) return;
    const used = new Set(accounts.map((a) => a.platform));
    const next = PLATFORMS.find((p) => !used.has(p)) ?? "other";
    setAccounts((arr) => [
      ...arr,
      {
        platform: next,
        handle: "",
        profile_url: "",
        insights_url: "",
        insights_state: "idle",
        followers: "",
      },
    ]);
  };

  const removeAccount = (i: number) => {
    if (accounts.length <= 1) return;
    setAccounts((arr) => arr.filter((_, idx) => idx !== i));
  };

  const makeMain = (i: number) => {
    if (i === 0) return;
    setAccounts((arr) => {
      const copy = [...arr];
      const [picked] = copy.splice(i, 1);
      copy.unshift(picked);
      return copy;
    });
  };

  const sortedCountries = React.useMemo(
    () =>
      [...COUNTRIES].sort((a, b) =>
        locale === "ar" ? a.ar.localeCompare(b.ar, "ar") : a.en.localeCompare(b.en, "en"),
      ),
    [locale],
  );

  const dialCountries = React.useMemo(
    () => sortedCountries.filter((c) => DIAL_CODES[c.code]),
    [sortedCountries],
  );

  async function uploadInsight(i: number, file: File) {
    if (file.size > 40 * 1024 * 1024) {
      toast.error(t("ambassadors.form.insights_error"));
      return;
    }
    updateAccount(i, { insights_state: "uploading" });
    try {
      const compressed = await compressImageBeforeUpload(file);
      const ext = (compressed.name.split(".").pop() ?? "bin").toLowerCase();
      const path = buildStoragePath("ambassador-media", "applications", { ext });
      const { error } = await supabase.storage
        .from("ambassador-insights")
        .upload(path, compressed, { contentType: compressed.type, cacheControl: "3600" });
      if (error) throw error;
      // Bucket is private: store the (non-public) object reference and preview locally.
      const objectUrl = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/ambassador-insights/${path}`;
      updateAccount(i, {
        insights_url: objectUrl,
        insights_preview: URL.createObjectURL(compressed),
        insights_state: "done",
      });
    } catch (err) {
      console.error(err);
      updateAccount(i, { insights_state: "error" });
      toast.error(t("ambassadors.form.insights_error"));
    }
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.display_name.trim()) {
      toast.error(t("ambassadors.form.name_required"));
      return;
    }
    if (!form.content_category) {
      toast.error(t("ambassadors.form.content_category_placeholder"));
      return;
    }
    const dial = getDialCode(form.phone_dial_country);
    const localDigits = form.phone_local.replace(/\D/g, "").replace(/^0+/, "");
    if (!localDigits) {
      toast.error(t("ambassadors.form.phone_required"));
      return;
    }
    const e164 = `${dial}${localDigits}`;
    const parsed = parsePhoneNumberFromString(
      e164,
      form.phone_dial_country as CountryCode,
    );
    if (!parsed || !parsed.isValid() || parsed.country !== form.phone_dial_country) {
      toast.error(t("ambassadors.form.phone_invalid"));
      return;
    }

    if (!termsAccepted) {
      toast.error(t("ambassadors.form.terms_required"));
      return;
    }

    setLoading(true);
    try {
      const res = await submit({
        data: {
          email: form.email,
          display_name: form.display_name.trim(),
          // النموذج مخصّص لشريحة Super Star — نُرسل الشريحة صريحة بدل الافتراضيّ.
          requested_tier: "elite" as const,
          whatsapp_e164: e164,
          content_category: form.content_category as ContentCategory,
          audience_accounts: accounts.map((a) => ({
            platform: a.platform,
            handle: a.handle.trim(),
            profile_url: normalizeProfileUrl(a.platform, a.profile_url) || undefined,
            insights_url: a.insights_url || undefined,
            followers: Number(a.followers) || 0,
          })),
          audience_country: form.audience_country,
          pitch: form.pitch || undefined,
          terms_accepted: true as const,
        },
      });
      setReferenceCode(res.referenceCode ?? null);
      setDone(res.accountCreated ? "new" : "existing");
    } catch (err: any) {
      const msg = String(err?.message ?? "");
      if (msg.includes("RATE_LIMITED")) toast.error(t("ambassadors.form.error_rate"));
      else toast.error(t("ambassadors.form.error_generic"));
    } finally {
      setLoading(false);
    }
  };


  return (
    <SiteShell>
      <PageHero
        pageKey="ambassadors"
        title={t("ambassadors.pageTitle")}
        image=""
        alt={t("ambassadors.pageTitle")}
      />
      <section className="mx-auto max-w-3xl px-4 py-12 md:py-16">
        {/* === دائرة حياة السفير === */}
        <AmbassadorLifeCircle />

        {/* === المراحل والفوائد === */}
        <AmbassadorStagesBenefits />

        {/* === UpPromote note — مخفيّ حاليّاً بقرار الإدارة (SHOW_UPPROMOTE_PUBLIC في linked-terms.ts) === */}
        {SHOW_UPPROMOTE_PUBLIC && (
        <section className="mb-10 rounded-none border border-border bg-card p-5 md:p-6">
          <h3 className="t-h3 mb-3 text-center">
            <LinkedTerms text={t("ambassadors.uppromote.title")} />
          </h3>
          <p className="t-body mx-auto mb-4 max-w-xl text-center text-muted-foreground">
            <LinkedTerms text={t("ambassadors.uppromote.body")} />
          </p>

          <div className="text-center">
            <Link
              to="/ambassadors/terms"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              {t("ambassadors.uppromote.termsCta")}
              <ArrowUp
                className={cn(
                  "h-3.5 w-3.5",
                  locale === "ar" ? "-rotate-90" : "rotate-90",
                )}
              />
            </Link>
          </div>
        </section>
        )}


        {/* === Tiers intro === */}
        <h2 className="t-h2 mb-3 text-center">{t("ambassadors.tiers.intro.title")}</h2>
        <p className="t-body text-muted-foreground text-center max-w-xl mx-auto mb-8">
          {t("ambassadors.tiers.intro.subtitle")}
        </p>

        <div
          ref={tiersContainerRef}
          className="mx-auto max-w-sm md:max-w-none grid gap-4 md:grid-cols-3 mb-12"
        >
          {([
            {
              key: "lover",
              Icon: Sparkles,
              cardStyle: {
                background: "var(--card)",
                borderColor: "color-mix(in oklab, var(--gold-soft) 60%, transparent)",
              },
              iconStyle: {
                background: "color-mix(in oklab, var(--gold-soft) 55%, white)",
                color: "var(--gold-deep)",
              },
              tagStyle: {
                background: "color-mix(in oklab, var(--gold-soft) 30%, white)",
                color: "var(--gold-deep)",
              },
              accentColor: "var(--gold-deep)",
            },
            {
              key: "ambassador",
              Icon: TrendingUp,
              cardStyle: {
                background: "var(--card)",
                borderColor: "color-mix(in oklab, var(--gold) 55%, transparent)",
              },
              iconStyle: {
                background: "color-mix(in oklab, var(--gold) 30%, white)",
                color: "var(--gold-deep)",
              },
              tagStyle: {
                background: "color-mix(in oklab, var(--gold) 22%, white)",
                color: "var(--gold-deep)",
              },
              accentColor: "var(--gold-deep)",
            },
            {
              key: "superstar",
              Icon: Crown,
              cardStyle: {
                background: "var(--card)",
                borderColor: "color-mix(in oklab, var(--primary) 55%, transparent)",
                boxShadow:
                  "0 10px 30px -18px color-mix(in oklab, var(--primary) 60%, transparent)",
              },
              iconStyle: {
                background: "color-mix(in oklab, var(--primary) 18%, white)",
                color: "var(--primary)",
              },
              tagStyle: {
                background: "color-mix(in oklab, var(--primary) 15%, white)",
                color: "var(--primary)",
              },
              accentColor: "var(--primary)",
            },
          ]).map(({ key, Icon, cardStyle, iconStyle, tagStyle, accentColor }) => {
            const tierImage =
              key === "lover"
                ? loverSocialImage
                : key === "ambassador"
                ? creatorImage
                : superstarImage;
            return (
              <details
                key={key}
                style={cardStyle}
                className="group rounded-none border overflow-hidden transition-shadow  [&[open]]:"
              >
                <summary
                  className="list-none cursor-pointer select-none [&::-webkit-details-marker]:hidden"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={typeof tierImage === "string" ? tierImage : tierImage.url}
                      alt={t(`ambassadors.tiers.${key}.name`)}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                    />
                  </div>
                  <div className="flex items-center gap-2 px-4 pt-4">
                    <span
                      style={iconStyle}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full"
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <h3 className="t-h4 flex-1">{t(`ambassadors.tiers.${key}.name`)}</h3>
                    <ChevronDown
                      className="h-4 w-4 text-muted-foreground transition-transform duration-300 group-open:rotate-180"
                    />
                  </div>
                  <div className="px-4 pb-4 pt-2">
                    <span
                      style={tagStyle}
                      className="inline-flex w-fit rounded-full px-2.5 py-1 text-[11px] font-medium"
                    >
                      {t(`ambassadors.tiers.${key}.tag`)}
                    </span>
                  </div>
                </summary>
                <div className="px-4 pb-5 flex flex-col gap-3 border-t border-border/40 pt-4">
                  <p className="text-sm text-foreground/75 leading-relaxed">
                    {t(`ambassadors.tiers.${key}.short`)}
                  </p>


                  <p
                    style={{ color: accentColor }}
                    className="text-sm font-medium leading-relaxed"
                  >
                    {t(`ambassadors.tiers.${key}.encourage`)}
                  </p>
                  <Link
                    to="/ambassadors/tier/$key"
                    params={{ key }}
                    style={{ color: accentColor }}
                    className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium hover:underline w-fit"
                  >
                    {t("ambassadors.tiers.read_more_about").replace(
                      "{name}",
                      t(`ambassadors.tiers.${key}.name`),
                    )}
                    <ArrowUp className={cn("h-3.5 w-3.5", locale === "ar" ? "-rotate-90" : "rotate-90")} />
                  </Link>
                </div>
              </details>
            );
          })}
        </div>

        {/* === Dual CTAs === */}
        <div className="grid gap-4 md:grid-cols-2 mb-12">
          {/* Primary: start as Lover */}
          <div className="rounded-none border border-primary/50 bg-primary/5 p-5 md:p-6 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h3 className="t-h4">
                {user ? t("ambassadors.cta.member_title") : t("ambassadors.cta.start_lover_title")}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed flex-1">
              {user ? t("ambassadors.cta.member_desc") : t("ambassadors.cta.start_lover_desc")}
            </p>
            {user ? (
              <Button asChild size="lg" className="w-full">
                <Link to="/account/ambassador">
                  {t("ambassadors.cta.open_dashboard_button")}
                </Link>
              </Button>
            ) : (
              <Button asChild size="lg" className="w-full">
                <Link
                  to="/auth"
                  search={{ mode: "signup", redirect: "/account/ambassador" }}
                >
                  {t("ambassadors.cta.start_lover_button")}
                </Link>
              </Button>
            )}
          </div>

          {/* Secondary: apply for Super Star */}
          <div className="rounded-none border border-border/60 bg-card p-5 md:p-6 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-muted-foreground" />
              <h3 className="t-h4">{t("ambassadors.cta.apply_superstar_title")}</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed flex-1">
              {t("ambassadors.cta.apply_superstar_desc")}
            </p>
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full"
              onClick={showForm ? () => setShowForm(false) : openSuperStarForm}
            >
              {showForm
                ? t("ambassadors.cta.hide_form")
                : t("ambassadors.cta.apply_superstar_button")}
            </Button>
          </div>
        </div>

        {/* WhatsApp quick contact */}
        <a
          href={`https://wa.me/${LECMO_WA_NUMBER}?text=${encodeURIComponent(t("ambassadors.whatsapp.prefill"))}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackWhatsAppClick("ambassadors_page", { intent: "questions_or_order" });
            void metaLazy(({ trackContact }) =>
              trackContact({ method: "whatsapp", location: "ambassadors_page" }),
            );
          }}

          data-analytics="whatsapp_click"
          data-analytics-location="ambassadors_page"
          className="mb-12 flex items-center gap-4 rounded-none border border-border/60 bg-card p-5 transition hover:bg-muted/40"
        >
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full wa-brand">
            <MessageCircle className="h-6 w-6" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block t-h4 mb-1">{t("ambassadors.whatsapp.title")}</span>
            <span className="block text-sm text-muted-foreground leading-relaxed">
              {t("ambassadors.whatsapp.body")}
            </span>
          </span>
          <span className="hidden sm:inline-flex shrink-0 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
            {t("ambassadors.whatsapp.cta")}
          </span>
        </a>

        {/* Form — hidden by default; shown when user clicks "Apply for Super Star" */}
        {showForm && (
        <div ref={formRef} className="rounded-none border border-border/60 bg-card p-6 md:p-8 scroll-mt-24">
          <h2 className="t-h3 mb-6">{t("ambassadors.form.title")}</h2>

          {done ? (

            <div className="rounded-none bg-muted/40 p-6 text-center space-y-4">
              {referenceCode && (
                <div className="inline-flex flex-col items-center gap-1">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t("ambassadors.form.reference_label")}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(referenceCode);
                      toast.success(t("ambassadors.form.reference_copied"));
                    }}
                    className="rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 font-mono text-base font-semibold text-primary hover:bg-primary/20"
                  >
                    {referenceCode}
                  </button>
                  <span className="text-xs text-muted-foreground">
                    {t("ambassadors.form.reference_hint")}
                  </span>
                </div>
              )}
              <p className="text-base leading-relaxed">
                {t(done === "new" ? "ambassadors.form.success_new_account" : "ambassadors.form.success_existing")}
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="amb-email">{t("ambassadors.form.email")} *</Label>
                  <Input
                    id="amb-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder={t("ambassadors.form.email_placeholder")}
                  />
                </div>
                <div>
                  <Label htmlFor="amb-name">{t("ambassadors.form.name")} *</Label>
                  <Input
                    id="amb-name"
                    required
                    maxLength={60}
                    value={form.display_name}
                    onChange={onChange("display_name")}
                    placeholder={t("ambassadors.form.name_placeholder")}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="amb-wa">{t("ambassadors.form.whatsapp")} *</Label>
                  <div className={`flex gap-2 ${locale === "ar" ? "flex-row-reverse" : ""}`}>
                    <Select
                      value={form.phone_dial_country}
                      onValueChange={(v) =>
                        setForm((f) => ({ ...f, phone_dial_country: v }))
                      }
                    >
                      <SelectTrigger
                        className="w-[140px] shrink-0"
                        aria-label={t("ambassadors.form.dial_code")}
                      >
                        <SelectValue>
                          {(() => {
                            const c = COUNTRIES.find((cc) => cc.code === form.phone_dial_country);
                            const dial = getDialCode(form.phone_dial_country);
                            return c ? (
                              <span className="inline-flex items-center gap-1.5">
                                <span>{c.flag}</span>
                                <span className="tabular-nums">{dial}</span>
                              </span>
                            ) : null;
                          })()}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {dialCountries.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            <span className="inline-flex items-center gap-2">
                              <span>{c.flag}</span>
                              <span className="tabular-nums text-muted-foreground">
                                {DIAL_CODES[c.code]}
                              </span>
                              <span className="text-xs">
                                {locale === "ar" ? c.ar : c.en}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="amb-wa"
                      type="tel"
                      required
                      inputMode="tel"
                      lang="en"
                      dir="ltr"
                      style={{ direction: "ltr" }}
                      pattern="[0-9]*"
                      value={form.phone_local}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          phone_local: e.target.value.replace(/[^\d]/g, ""),
                        }))
                      }
                      placeholder={t("ambassadors.form.whatsapp_placeholder")}
                      className="tabular-nums"
                    />
                  </div>
                </div>
                <div>
                  <Label>{t("ambassadors.form.country")} *</Label>
                  <Select
                    value={form.audience_country}
                    onValueChange={(v) => setForm((f) => ({ ...f, audience_country: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("ambassadors.form.country_placeholder")} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {sortedCountries.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.flag} {locale === "ar" ? c.ar : c.en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>{t("ambassadors.form.content_category")} *</Label>
                <Select
                  value={form.content_category}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, content_category: v as ContentCategory }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("ambassadors.form.content_category_placeholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {t(`ambassadors.form.category.${c}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Social accounts — قائمة ديناميكيّة */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>{t("ambassadors.form.accounts_title")} *</Label>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {accounts.length}/8
                  </span>
                </div>

                {accounts.map((acc, i) => {
                  const usedByOthers = new Set(
                    accounts.filter((_, idx) => idx !== i).map((a) => a.platform),
                  );
                  const isMain = i === 0;
                  const previewUrl =
                    normalizeProfileUrl(acc.platform, acc.profile_url) ||
                    buildProfileUrl(acc.platform, acc.handle) ||
                    "";

                  return (
                    <div
                      key={i}
                      className={
                        "rounded-none border bg-background/40 p-3 space-y-3 " +
                        (isMain ? "border-primary/60 ring-1 ring-primary/20" : "border-border/60")
                      }
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <PlatformIcon platform={acc.platform} className="h-6 w-6" />
                          {isMain ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                              <Star className="h-3 w-3" />
                              {t("ambassadors.form.main_badge")}
                            </span>
                          ) : (
                            <span className="text-xs font-medium text-muted-foreground">
                              {t("ambassadors.form.account_n", { n: String(i + 1) })}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-1">
                          {!isMain && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => makeMain(i)}
                              className="h-7 px-2 text-xs text-muted-foreground hover:text-primary"
                              aria-label={t("ambassadors.form.make_main")}
                            >
                              <ArrowUp className="h-3.5 w-3.5 me-1" />
                              {t("ambassadors.form.make_main")}
                            </Button>
                          )}
                          {accounts.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeAccount(i)}
                              className="h-7 px-2 text-muted-foreground hover:text-destructive"
                              aria-label={t("ambassadors.form.remove_account")}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <Label className="text-xs">{t("ambassadors.form.platform")}</Label>
                          <Select
                            value={acc.platform}
                            onValueChange={(v) => updateAccount(i, { platform: v as Platform })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PLATFORMS.map((p) => (
                                <SelectItem
                                  key={p}
                                  value={p}
                                  disabled={
                                    p !== acc.platform && usedByOthers.has(p) && p !== "other"
                                  }
                                >
                                  <span className="inline-flex items-center gap-2">
                                    <PlatformIcon platform={p} className="h-4 w-4" />
                                    {t(`ambassadors.platforms.${p}`)}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">{t("ambassadors.form.followers")}</Label>
                          <Input
                            type="number"
                            inputMode="numeric"
                            lang="en"
                            dir="ltr"
                            style={{ direction: "ltr" }}
                            min={0}
                            required
                            className="tabular-nums"
                            value={acc.followers}
                            onChange={(e) => updateAccount(i, { followers: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <Label className="text-xs">{t("ambassadors.form.handle")} *</Label>
                          <Input
                            required
                            maxLength={100}
                            value={acc.handle}
                            onChange={(e) => updateAccount(i, { handle: e.target.value })}
                            placeholder={t("ambassadors.form.handle_placeholder")}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{t("ambassadors.form.profile_url")}</Label>
                          <Input
                            type="text"
                            inputMode="url"
                            autoCapitalize="none"
                            autoCorrect="off"
                            maxLength={500}
                            value={acc.profile_url}
                            onChange={(e) => updateAccount(i, { profile_url: e.target.value })}
                            placeholder={t("ambassadors.form.profile_url_placeholder")}
                          />
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {t("ambassadors.form.profile_url_hint")}
                          </p>
                          {previewUrl && (
                            <a
                              href={previewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-1 inline-flex min-h-[36px] items-center gap-1 text-[11px] text-primary hover:underline break-all"
                            >
                              <ExternalLink className="h-3 w-3 shrink-0" />
                              <span className="truncate">{previewUrl.replace(/^https?:\/\//, "")}</span>
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Insights upload */}
                      <div>
                        <Label className="text-xs">{t("ambassadors.form.insights")}</Label>
                        <div className="mt-1 flex items-center gap-2">
                          <label
                            className={cn(
                              "inline-flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-none border border-dashed border-border bg-background px-3 py-2 text-xs transition hover:bg-muted/40",
                              acc.insights_state === "uploading" && "opacity-60",
                            )}
                          >
                            {acc.insights_state === "uploading" ? (
                              <>
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                {t("ambassadors.form.insights_uploading")}
                              </>
                            ) : acc.insights_state === "done" && acc.insights_url ? (
                              <>
                                <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                                {t("ambassadors.form.insights_uploaded")}
                              </>
                            ) : (
                              <>
                                <Upload className="h-3.5 w-3.5" />
                                {t("ambassadors.form.insights_hint")}
                              </>
                            )}
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp,image/heic,image/heif,application/pdf"
                              className="hidden"
                              disabled={acc.insights_state === "uploading"}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) uploadInsight(i, file);
                                e.target.value = "";
                              }}
                            />
                          </label>
                          {acc.insights_url && (
                            <>
                              <a
                                href={acc.insights_preview ?? acc.insights_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-muted-foreground hover:text-destructive"
                                onClick={() =>
                                  updateAccount(i, {
                                    insights_url: "",
                                    insights_preview: undefined,
                                    insights_state: "idle",
                                  })
                                }
                                aria-label={t("ambassadors.form.insights_remove")}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {accounts.length < 8 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addAccount}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 me-2" />
                    {t("ambassadors.form.add_account")}
                  </Button>
                )}
              </div>

              <div>
                <Label htmlFor="amb-pitch">{t("ambassadors.form.pitch")}</Label>
                <Textarea
                  id="amb-pitch"
                  maxLength={500}
                  rows={4}
                  value={form.pitch}
                  onChange={onChange("pitch")}
                  placeholder={t("ambassadors.form.pitch_placeholder")}
                />
                <p className="text-xs text-muted-foreground mt-1 text-end tabular-nums">
                  {t("ambassadors.form.pitch_count", { count: String(form.pitch.length) })}
                </p>
              </div>

              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--rule-accent)]"
                />
                <span className="text-muted-foreground">
                  {t("ambassadors.form.terms_agree")}{" "}
                  <Link
                    to="/ambassadors/terms"
                    target="_blank"
                    className="text-foreground underline underline-offset-4"
                  >
                    {t("ambassadors.form.terms_link")}
                  </Link>
                </span>
              </label>

              <Button type="submit" disabled={loading || !termsAccepted} className="w-full" size="lg">
                {loading ? t("ambassadors.form.submitting") : t("ambassadors.form.submit")}
              </Button>
            </form>
          )}
        </div>
        )}
      </section>
    </SiteShell>
  );
}
